import _, { delay } from 'lodash'
import {ActionTree} from 'vuex';
import {UserState} from '@/modules/user/store/index';
import {RootState} from '@/store/types';
import {PaginationInput} from '@/modules/core/model/pagination'
import {MutationTypes} from '@/modules/user/store/mutations'
import * as api from '@/modules/user/api'
import User from '@/modules/user/model/User';
import {DeleteSessionsCriteria} from '@/modules/user/api';
import UserGroup from '@/modules/user/model/UserGroup';

const actions: ActionTree<UserState, RootState> = {
    /**
     * Solicita a la api una paginación de usuarios
     *
     * @param commit
     * @param input
     */
    async loadPagination ({commit}, input: PaginationInput) {
        const pagination = await api.getUserPagination(input)

        input.name
            ? commit(MutationTypes.SetNamedPagination, { name: input.name, pagination})
            : commit(MutationTypes.SetUsersPagination, pagination)
    },

    /**
     * Carga un usuario en el estado de la aplicación
     *
     * @param commit
     * @param id
     */
    async loadUser ({commit}, id: number | 'nuevo') {
        if (id === 'nuevo') {
            commit(MutationTypes.SetCurrentUser, new User())

            return
        }

        commit(MutationTypes.SetCurrentUser, await api.getUser(id))
    },

    /**
     * Guarda un usuario
     *
     * Crea o actualiza el usuario que esta actualmente en edición
     */
    async saveUser ({state, commit}) {
        let user = state.currentUser
        user = user.isNew
            ? await api.createUser(user)
            : await api.updateUser(user)

        commit(MutationTypes.SetCurrentUser, user)
    },

    /**
     * Cambia la contraseña del usuario actualmente en edición
     *
     * @param state
     * @param payload
     */
    async changeUserPassword ({state}, payload: {password: string, confirm: string, clearSessions: boolean}) {
        if (state.currentUser.isNew) {
            throw new Error('No se puede actualizar la contraseña de un usuario que todavía no ha sido registrado')
        }

        await api.changePassword({ ...payload, user: state.currentUser.id })
    },

    /**
     * Se ejecuta antes de eliminar el usuario actualmente en edición
     *
     * @param state
     */
     async beforeDeleteUser ({state}, payload: {delay: number}) {
         // return await setTimeout(() => {console.log('beforeDeleteUser')}, payload.delay)
        const d = (payload && payload.delay) ? payload.delay : 0;
        if (state.tasksBeforeDeleteUser.size > 0) {
            // state.tasksBeforeDeleteUser.forEach(async value => {await value()})
            state.tasksBeforeDeleteUser.forEach(async value => {
               await value()
            })
        }
        await sleep(d)
    },

    /**
     * Elimina el usuario actualmente en edición
     *
     * @param state
     */
    async deleteUser ({state}) {
        await api.deleteUser(state.currentUser.id)
    },

    /**
     * Carga en el estado de la aplicación una lista de sesiones según los filtros
     * argumentados
     *
     * @param commit
     * @param input
     */
    async loadSessionPagination ({commit}, input: PaginationInput<{user: number | undefined}>) {
        commit(MutationTypes.SetSessionPagination, await api.getSessionPagination(input))
    },

    /**
     * Solicita al servidor la eliminación de sesiones en base a los criterios
     * argumentados
     *
     * @param context
     * @param criteria
     */
    async deleteSessions (context, criteria: DeleteSessionsCriteria) {
        return await api.deleteSessions(criteria)
    },

    /**
     * Comprueba si el correo electrónico se encuentra disponible
     *
     * @param context
     * @param email
     */
    async checkEmailAvailability (context, email: string) {
        return await api.checkEmailAvailability(email)
    },

    /**
     * Solicita a la API de artisan la lista de grupos de usuarios registrados y carga
     * dicha información en el estado de la aplicación
     */
    async loadUserGroups ({state, commit}, payload = {force: false}) {
        if (state.userGroupsFetched && !payload.force) {
            return
        }

        commit(MutationTypes.SetUserGroups, await api.listUserGroups())
        commit(MutationTypes.ToggleUserGroupsFetched, true)
    },

    /**
     * Carga un grupo de usuarios
     *
     * @param state
     * @param commit
     * @param id
     */
    async loadUserGroup ({state, commit}, id: number) {
        // Se ha solicitado crear un nuevo grupo de usuarios
        if (id === 0) {
            commit(MutationTypes.SetCurrentUserGroup, new UserGroup())

            return
        }

        const fetchedGroup = state.userGroups.find(g => g.id === id)
        commit(
            MutationTypes.SetCurrentUserGroup,
            fetchedGroup ? _.cloneDeep(fetchedGroup) : await api.getUserGroup(id),
        )
    },

    /**
     * Añade al grupo actual la lista de usuarios seleccionados desde la
     * pantalla para añadir usuarios
     *
     * @param state
     * @param commit
     */
    async addUsersToGroup ({state, commit}) {
        await api.addUsersToGroup(
            state.userGroup.id,
            state.selectedGroupUsers.map(u => u.id),
        )
    },

    /**
     * Quita del grupo actualmente seleccionada la lista de usuarios seleccionados
     *
     * @param state
     * @param commit
     */
    async deleteGroupUsers ({state, commit}) {
        // El grupo de usuarios es nuevo, todavía no ha sido insertado en el servidor.
        // Se eliminarán los usuarios coincidentes de la lista de usuarios que
        // serán introducidos al guardar. Dicha colección se encuentra en
        // la entidad del grupo
        if (state.userGroup.isNew) {
            const newUsers = _.clone(state.userGroup.newUsers)
            state.selectedGroupUsers.forEach(selected => {
                const index = newUsers.findIndex(newUser => newUser.id === selected.id)
                if (index !== -1) {
                    newUsers.splice(index, 1)
                }
            })

            commit(MutationTypes.UpdateUserGroupField, {
                field: 'newUsers',
                value: newUsers,
            })
        } else {
            await api.deleteGroupUsers(
                state.userGroup.id,
                state.selectedGroupUsers.map(u => u.id),
            )
        }

        commit(MutationTypes.SetSelectedGroupUsers, [])
    },

    /**
     * Actualiza la información del grupo de usuarios actualmente en edición
     */
    async saveUserGroup ({state, commit}) {
        let group = state.userGroup
        group = group.isNew
            ? await api.createUserGroup(group)
            : await api.updateUserGroup(group)

        commit(MutationTypes.SetCurrentUserGroup, group)
        commit(MutationTypes.AddUserGroup, group)
    },

    /**
     * Elimina el grupo de usuarios actualmente seleccionado
     */
    async deleteUserGroup ({state, commit}) {
        const id = state.userGroup.id
        const isDeleteAllUser = state.checkingiDeleteFromBBDDUnigest
        await api.deleteUseGroup(id, isDeleteAllUser)

        commit(MutationTypes.SetCurrentUserGroup, new UserGroup())
        // Si la aplicación ya ha obtenido la lista de grupos de usuarios eliminará el grupo
        // de la memoria. Esto evita tener que realizar otra petición al servidor para
        // obtener la lista actualizada
        if (state.userGroupsFetched) {
            commit(MutationTypes.DeleteUserGroup, id)
        }
    },

    /**
     * Se ejecuta antes de eliminar un grupo actualmente en seleccionado
     *
     * @param state
     */
     async beforeDeleteUserGroup ({state}, payload: {delay: number}) {
        /*if (payload && payload.delay > 0 ) { return await sleep(payload.delay)} */
        const d = (payload && payload.delay) ? payload.delay : 0
        if (state.tasksBeforeDeleteUserGroup.size > 0) {
            state.tasksBeforeDeleteUserGroup.forEach(async value => {
               await value()
            })
        }
        await sleep(d)
   },
}

export default actions

/**
 * Realiza una pausa x milisegundos
 * @param ms milisegundso
 * @returns any
 */
function timeout (ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Realiza una pausa x milisegundos de restraso
 * @param delay milisegundso de retraso
 */
export async function sleep (delay: number) {
    await timeout(delay)
}
